import { Button, LogIn, LogOut, decorativeIcon } from '@saniplus/ui';
import { useTranslation } from 'react-i18next';

/**
 * THE ACTION SLOT. One shape, four forms, always in the thumb zone.
 *
 * `W3-home-states/STATES.md` fixes the rules this component exists to enforce:
 *
 * | Form | Anatomy |
 * |---|---|
 * | `action`   | the 88 px key, action fill, bevel, 25 px label + consequence |
 * | `disabled` | no fill, no bevel, **plus a sentence saying when it opens** |
 * | `loading`  | keeps the fill and the bevel, swaps the label, `aria-busy` — never disabled-looking mid-request |
 *
 * **Height, C-19 (2026-09-03).** All three forms render at `--spacing-touch-lg`
 * (56 px floor, ~72 px with both lines) in every orientation. The 88 px
 * `--spacing-key-landscape` before it was still read as "a big btn when it can
 * be smaller" on a real iPhone 12, where browser chrome leaves ~630 px of
 * viewport. 56 px is the glove floor plus a step; the label stays `headline`
 * so the key keeps its rank. `PunchOutGate` matches it so the gated and
 * ungated OUT states never jump. `DESIGN.md`'s "Corrections from the build"
 * table has the dated entry.
 *
 * **Never IN and OUT at once.** The type is a single discriminated union
 * precisely so a screen cannot render two punch actions: there is one slot and
 * it takes one form.
 *
 * The disabled form deliberately does *not* use `--color-ink-disabled`
 * (#4C5153, 2.4:1 on the night canvas). Disabled controls are exempt from
 * WCAG, but this one is read at 02:00 in a plant basement, so the label uses
 * muted ink at 5.95:1 and the control reads as disabled through the absent
 * fill and absent bevel instead.
 */
export type PunchKeyProps =
  | {
      readonly form: 'action';
      readonly type: 'in' | 'out';
      /** Already translated. */
      readonly label: string;
      readonly subLabel: string;
      readonly onPunch: () => void;
    }
  | {
      readonly form: 'disabled';
      readonly type: 'in' | 'out';
      readonly label: string;
      readonly subLabel: string;
      /** The sentence beneath the key: when it opens, and that it opens itself. */
      readonly note: string;
    }
  | {
      readonly form: 'loading';
      readonly type: 'in' | 'out';
      readonly label: string;
      readonly subLabel: string;
    };

export function PunchKey(props: PunchKeyProps) {
  const { t } = useTranslation('punch');
  const Icon = props.type === 'in' ? LogIn : LogOut;
  const icon = <Icon {...decorativeIcon('xl')} />;

  if (props.form === 'disabled') {
    return (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          disabled
          data-testid="punch-key"
          className="flex min-h-touch-lg w-full cursor-not-allowed flex-col items-center justify-center gap-1 rounded-control border border-border-field bg-surface-sunk px-6 py-2 text-headline text-ink-muted"
        >
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="inline-flex shrink-0">
              {icon}
            </span>
            <span>{props.label}</span>
          </span>
          <span className="text-body-compact font-medium">{props.subLabel}</span>
        </button>
        <p className="px-1 text-center text-body text-ink-muted">{props.note}</p>
      </div>
    );
  }

  if (props.form === 'loading') {
    return (
      <Button
        size="xl"
        variant="primary"
        loading
        loadingLabel={t('action.sending')}
        subLabel={props.subLabel}
        className="min-h-touch-lg py-2"
        data-testid="punch-key"
      >
        {props.label}
      </Button>
    );
  }

  return (
    <Button
      size="xl"
      variant="primary"
      iconStart={icon}
      subLabel={props.subLabel}
      onClick={props.onPunch}
      /*
       * C-19: `--spacing-touch-lg`, not `size="xl"`'s 112 px. `cn()` has
       * `min-h-*` in its named-size group, so this replaces the variant's
       * class rather than racing it in stylesheet order.
       */
      className="min-h-touch-lg py-2"
      data-testid="punch-key"
    >
      {props.label}
    </Button>
  );
}
